import type { Word } from "../types";

const DAY = 86_400_000;
export type Rating = "again" | "hard" | "good" | "easy";
export const RATINGS: readonly Rating[] = ["again", "hard", "good", "easy"];
export interface CardRecord { interval: number; due: number; rating: Rating; repetitions: number }
export type CardRecords = Record<string, CardRecord>;

export interface FlashcardSession {
  current(): Word | null;
  remaining(): number;
  completed(): number;
  rate(rating: Rating, at?: number): void;
}

/**
 * A reveal-and-rate queue over `words`. Ratings schedule the next review and are written to `records`
 * (the learner's `progress.flashcards`); "again" puts the card back at the end of this session.
 * With `dueOnly`, cards that are new or already due are queued.
 */
export function createFlashcardSession(words: Word[], records: CardRecords, { dueOnly = false, now = Date.now() } = {}): FlashcardSession {
  const queue = words.filter((w) => !dueOnly || !records[w.id] || records[w.id]!.due <= now);
  let completed = 0;
  return {
    current: () => queue[0] ?? null,
    remaining: () => queue.length,
    completed: () => completed,
    rate(rating, at = Date.now()) {
      if (!RATINGS.includes(rating)) throw new Error("Unknown flashcard rating");
      const word = queue.shift();
      if (!word) return;
      const previous: Partial<CardRecord> = records[word.id] ?? {};
      const last = previous.interval ?? 0;
      const interval = rating === "again" ? 60_000
        : rating === "hard" ? Math.max(600_000, last * 1.2)
        : rating === "good" ? Math.max(DAY, last * 2)
        : Math.max(4 * DAY, last * 3);
      records[word.id] = { ...previous, interval, due: at + interval, rating, repetitions: (previous.repetitions ?? 0) + 1 };
      if (rating === "again") queue.push(word); else completed++;
    },
  };
}

export const dueCount = (words: Word[], records: CardRecords | undefined, now = Date.now()) =>
  words.filter((w) => !records?.[w.id] || records[w.id]!.due <= now).length;
